import {Component, computed, effect, HostListener, inject, input, output, signal, viewChild, TemplateRef, ViewContainerRef} from '@angular/core';
import {distinctUntilChanged, map, Observable, of, sampleTime, switchMap, tap} from 'rxjs';
import {AsyncPipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {toObservable} from '@angular/core/rxjs-interop';
import {SearchService} from '../../../services/search.service';
import {OverlayService} from '../../../services/overlay.service';
import {entityId, EntityId} from '../../../models/entity.model';
import {EntityCardComponent} from '../entity-card/entity-card.component';
import {PackItemModel} from '../../../models/pack-item.model';
import {OverlayRef} from '@angular/cdk/overlay';

export interface SearchSelectedEvent {
  item: PackItemModel
}

@Component({
  selector: 'app-search',
  imports: [
    AsyncPipe,
    FormsModule,
    EntityCardComponent
  ],
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss',
  host: {
    class: 'app-block',
    '(focusin)': 'onFocusIn()',
    '(focusout)': 'onFocusOut($event)'
  }
})
export class SearchComponent {
  readonly scope = input<'packs' | 'users' | 'prompts'>('packs')
  readonly placeholder = input<string>('Search...')
  readonly exclude = input(
    null,
    {transform: (val: readonly EntityId[] | null) => val ? new Set(val.map(id => entityId(id))) : null}
  )
  readonly selected = output<SearchSelectedEvent>()
  readonly element = viewChild.required<HTMLElement>('searchInput')
  readonly resultsTemplate = viewChild.required<TemplateRef<any>>('resultsTemplate')

  protected readonly minSearchLength = 3
  protected readonly resultsObservable: Observable<PackItemModel[] | null>
  protected readonly results = signal<PackItemModel[] | null>(null)
  protected readonly selectedResult = signal<PackItemModel | null>(null)
  protected readonly selectedIndex = signal<number>(-1)
  protected readonly search = signal<string>('')
  protected readonly searchSubject = toObservable(this.search)
  protected readonly focused = signal(false)
  protected readonly active = computed(() => this.focused() && this.search().length > 0)

  private readonly searchService = inject(SearchService)
  private readonly overlayService = inject(OverlayService)
  private readonly viewContainerRef = inject(ViewContainerRef)
  private overlayRef?: OverlayRef

  constructor() {
    // Update selected index when results change
    effect(() => {
      const results = this.results()
      if (results && results.length > 0) {
        let selected = this.selectedResult()
        let index = 0
        if (selected) index = Math.max(0, results.findIndex(it => it.id === selected!.id))
        this.selectedIndex.set(index)
      } else {
        this.selectedIndex.set(-1)
      }
    })

    // Update selected result when selected index changes
    effect(() => {
      const index = this.selectedIndex()
      if (index >= 0) {
        this.selectedResult.set(this.results()![index] || null)
      } else {
        this.selectedResult.set(null)
      }
    })

    // Manage overlay visibility based on active state
    effect(() => {
      const isActive = this.active()
      if (isActive && !this.overlayRef) {
        this.showOverlay()
      } else if (!isActive && this.overlayRef) {
        this.hideOverlay()
      }
    })

    // Search for prompts when the search text changes
    this.resultsObservable = this.searchSubject.pipe(
      sampleTime(500),
      distinctUntilChanged(),
      switchMap(query => query.length >= this.minSearchLength ? this.searchService.search(query) : of(null)),
      map(results => {
        if (results) {
          return this.exclude() ? results.items.filter(it => !this.exclude()!.has(it.id)) : results.items
        }
        return null
      }),
      tap(results => this.results.set(results))
    )
  }

  protected onFocusIn() {
    this.focused.set(true)
  }

  protected onFocusOut(event: FocusEvent) {
    // Delay to allow click event to fire first
    setTimeout(() => {
      this.focused.set(false)
    }, 200)
  }

  // Move selection up or down
  protected moveSelection(next: boolean) {
    if (next) {
      if (this.selectedIndex() < this.results()!.length - 1) {
        this.selectedIndex.update(val => val + 1)
      }
    } else {
      if (this.selectedIndex() > 0) {
        this.selectedIndex.update(val => val - 1)
      }
    }
  }

  // Select the currently selected result
  protected selectResult(item: PackItemModel | null = this.selectedResult()) {
    if (item) {
      this.reset()
      this.selected.emit({item})
    }
  }

  private reset() {
    this.search.set('')
    this.results.set(null)
    this.selectedResult.set(null)
    this.selectedIndex.set(-1)
  }

  private showOverlay() {
    const input = this.element()
    const template = this.resultsTemplate()
    
    this.overlayRef = this.overlayService.createOverlay(template, {
      origin: input,
      hasBackdrop: false,
      maxHeight: '400px'
    }, this.viewContainerRef)
    
    // Update width to match input after overlay is attached
    this.overlayRef.updateSize({
      width: input.getBoundingClientRect().width
    })
    
    // Prevent blur when clicking in overlay
    this.overlayRef.overlayElement.addEventListener('mousedown', (e) => {
      e.preventDefault()
    })
  }

  private hideOverlay() {
    if (this.overlayRef) {
      this.overlayRef.dispose()
      this.overlayRef = undefined
    }
  }

  ngOnDestroy() {
    this.hideOverlay()
  }
}
